
import React, { useEffect, useRef, useState, useMemo } from 'react';
import * as echarts from 'echarts';

const SimulateStrategy = ({ result, onBack }) => {
  const chartRef = useRef(null);
  const [simIndex, setSimIndex] = useState(0);
  const [scrollStart, setScrollStart] = useState(0);
  const [isSimulating, setIsSimulating] = useState(false);
  const [speed, setSpeed] = useState(1000);
  const [cumPnl, setCumPnl] = useState(0);
  const timer = useRef(null);

  // Prepare chart data (memoized)
  const candles = useMemo(() => result?.candles || [], [result]);
  const trades = useMemo(() => result?.trades || [], [result]);
  const hasData = candles.length > 0;


  // Theme detection (default white, switch to black if user changes)
  const [theme, setTheme] = useState('light');
  useEffect(() => {
    // Try to detect theme from body or window (simple heuristic)
    const observer = new MutationObserver(() => {
      if (document.body.classList.contains('dark') || document.body.style.background === 'black') {
        setTheme('dark');
      } else {
        setTheme('light');
      }
    });
    observer.observe(document.body, { attributes: true, attributeFilter: ['class', 'style'] });
    // Initial check
    if (document.body.classList.contains('dark') || document.body.style.background === 'black') {
      setTheme('dark');
    } else {
      setTheme('light');
    }
    return () => observer.disconnect();
  }, []);

  // Prepare trade markers for ECharts (with entry/exit price text)
  const tradeMarkers = useMemo(() => {
    const labelColor = theme === 'dark' ? '#fff' : '#111';
    const improvedMarkers = [];
    for (let i = 0; i < trades.length; ++i) {
      const t = trades[i];
      // Only show marker if entry/exit is within visible candles
      const entryIdx = candles.findIndex(c => c[0] === t.entry_date);
      if (entryIdx !== -1 && entryIdx <= simIndex) {
        const candle = candles[entryIdx];
        // Place buy marker at Low - 0.5%
        const low = candle[3];
        const markerY = low - (low * 0.005);
        improvedMarkers.push({
          name: 'Buy',
          value: 'Buy',
          xAxis: new Date(candle[0]).toLocaleDateString('en-GB'),
          yAxis: markerY,
          symbol: 'triangle',
          symbolRotate: 0,
          symbolSize: 18,
          itemStyle: { color: '#00da3c' },
          label: {
            show: true,
            formatter: `Entry @ ${t.entry_price.toFixed(2)}`,
            color: labelColor,
            fontWeight: 700,
            fontSize: 14,
            position: 'bottom',
            distance: 8,
          },
        });
      }
      const exitIdx = candles.findIndex(c => c[0] === t.exit_date);
      if (exitIdx !== -1 && exitIdx <= simIndex) {
        const candle = candles[exitIdx];
        // Place sell marker at High + 0.5%
        const high = candle[2];
        const markerY = high + (high * 0.005);
        improvedMarkers.push({
          name: 'Sell',
          value: 'Sell',
          xAxis: new Date(candle[0]).toLocaleDateString('en-GB'),
          yAxis: markerY,
          symbol: 'triangle',
          symbolRotate: 180,
          symbolSize: 18,
          itemStyle: { color: '#ec0000' },
          label: {
            show: true,
            formatter: `Exit @ ${t.exit_price.toFixed(2)}`,
            color: labelColor,
            fontWeight: 700,
            fontSize: 14,
            position: 'top',
            distance: 8,
          },
        });
      }
    }
    return improvedMarkers;
  }, [trades, candles, simIndex, theme]);

  // Responsive: show max 20 candles, scrollable window
  const maxCandles = 16;
  // Render ECharts chart
  useEffect(() => {
    if (!hasData) return;
    const chart = echarts.getInstanceByDom(chartRef.current) || echarts.init(chartRef.current);
    // Responsive: show max 20 candles, scrollable window
    let start = scrollStart;
    let end = Math.min(start + maxCandles, candles.length);
    // If simIndex moves past end, scroll window right
    if (simIndex >= end) {
      start = simIndex - maxCandles + 1;
      end = simIndex + 1;
      setScrollStart(start);
    }
    // If simIndex moves left, scroll window left
    if (simIndex < start) {
      start = Math.max(0, simIndex - 1);
      end = start + maxCandles;
      setScrollStart(start);
    }
    const visibleCandles = candles.slice(start, end);

    // ECharts: color0 is for open > close (down, red), color is for open < close (up, green)
    const upColor = '#00da3c';
    const upBorderColor = '#008F28';
    const downColor = '#ec0000';
    const downBorderColor = '#8A0000';

    // Calculate y-axis min/max for scaling: 10% below/above last close
    let lastClose = visibleCandles.length > 0 ? visibleCandles[visibleCandles.length - 1][4] : 0;
    let yMin = lastClose * 0.90;
    let yMax = lastClose * 1.10;
    // If all closes are the same, add a small buffer
    if (yMin === yMax) {
      yMin = lastClose * 0.95;
      yMax = lastClose * 1.05;
    }

    chart.setOption({
      backgroundColor: theme === 'dark' ? '#181c23' : '#fff',
      animation: false,
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'cross' },
        backgroundColor: theme === 'dark' ? '#222' : '#fff',
        borderColor: '#aaa',
        borderWidth: 1,
        textStyle: { color: theme === 'dark' ? '#fff' : '#111' },
      },
      xAxis: {
        type: 'category',
        data: visibleCandles.map(c => new Date(c[0]).toLocaleDateString('en-GB')),
        scale: true,
        boundaryGap: false,
        axisLine: { onZero: false, lineStyle: { color: theme === 'dark' ? '#fff' : '#111' } },
        splitLine: { show: false },
        min: 0,
        max: visibleCandles.length - 1,
        axisLabel: { fontSize: 12 },
      },
      yAxis: {
        scale: true,
        min: yMin,
        max: yMax,
        axisLine: { lineStyle: { color: theme === 'dark' ? '#fff' : '#111' } },
        splitLine: { show: true, lineStyle: { color: theme === 'dark' ? '#333' : '#eee' } },
        axisLabel: {
          formatter: function (val) {
            return Number(val).toFixed(2);
          },
          fontSize: 14,
        },
      },
      series: [
        {
          type: 'candlestick',
          data: visibleCandles.map(c => [c[1], c[4], c[2], c[3]]), // [open, close, low, high]
          itemStyle: {
            color: upColor,
            color0: downColor,
            borderColor: upColor, // wick color same as body (up)
            borderColor0: downColor, // wick color same as body (down)
            shadowColor: 'transparent',
          },
          emphasis: {
            itemStyle: {
              shadowBlur: 0,
              borderWidth: 1,
            }
          },
          markPoint: {
            data: tradeMarkers.filter(m => {
              // Only show markers in visible window
              return visibleCandles.some(c => new Date(c[0]).toLocaleDateString('en-GB') === m.xAxis);
            }),
            symbolKeepAspect: true,
          },
        },
      ],
      grid: { left: 60, right: 60, top: 40, bottom: 40 },
    });
    chart.resize();
    // Clean up chart instance on unmount
    return () => { if (chart) chart.dispose(); };
  }, [candles, simIndex, tradeMarkers, theme, hasData, scrollStart]);
  // Simulate playback
  // Calculate cumulative PnL per candle (date) up to simIndex
  useEffect(() => {
    if (!hasData) return;
    if (isSimulating && simIndex < candles.length - 1) {
      timer.current = setTimeout(() => setSimIndex(i => i + 1), speed);
    } else if (!isSimulating) {
      if (timer.current) clearTimeout(timer.current);
    }
    // Cumulative PnL up to current simIndex
    let total = 0;
    for (let t of trades) {
      const exitIdx = candles.findIndex(c => c[0] === t.exit_date);
      if (exitIdx !== -1 && exitIdx <= simIndex) {
        total += t.pnl;
      }
    }
    setCumPnl(total);
    return () => timer.current && clearTimeout(timer.current);
  }, [isSimulating, simIndex, speed, candles, trades, hasData]);

  const handlePlay = () => {
    if (!hasData) return;
    if (simIndex >= candles.length - 1) setSimIndex(0);
    setIsSimulating(true);
  };
  const handlePause = () => setIsSimulating(false);
  const handleStop = () => { setIsSimulating(false); setSimIndex(0); };

  return (
    <div style={{ padding: 24 }}>
      <button onClick={onBack} style={{ marginBottom: 16 }}>← Back to Results</button>
      <h2>Algo Simulator</h2>
      {!hasData && (
        <div style={{
          background: '#fff3cd',
          color: '#856404',
          border: '1px solid #ffeeba',
          borderRadius: 6,
          padding: '16px',
          marginBottom: 24,
          fontWeight: 500,
          fontSize: 16
        }}>
          ⚠️ Simulation data is missing or empty. Please check your backtest results or try again.
        </div>
      )}
  <div style={{ width: '100%', maxWidth: 1600, margin: '0 auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 8 }}>
          <button onClick={() => setScrollStart(s => Math.max(0, s - 1))} disabled={scrollStart === 0} style={{ fontSize: 24, marginRight: 8 }}>◀</button>
          <span style={{ fontSize: 16 }}>Scroll</span>
          <button onClick={() => setScrollStart(s => Math.min(candles.length - maxCandles, s + 1))} disabled={scrollStart >= candles.length - maxCandles} style={{ fontSize: 24, marginLeft: 8 }}>▶</button>
        </div>
  <div ref={chartRef} style={{ width: '100%', minWidth: 1200, height: 600, background: '#181c23', borderRadius: 12, marginBottom: 24, overflowX: 'auto' }} />
        <div style={{ margin: '16px 0', display: 'flex', alignItems: 'center', gap: 16, justifyContent: 'center' }}>
          <button onClick={handlePlay} disabled={!hasData || isSimulating || simIndex >= candles.length - 1}
            style={{ background: '#4285f4', color: '#fff', border: 'none', borderRadius: 6, padding: '8px 24px', fontSize: 18, fontWeight: 600, cursor: 'pointer', opacity: (!hasData || isSimulating || simIndex >= candles.length - 1) ? 0.5 : 1 }}>
            ▶ Play
          </button>
          <button onClick={handlePause} disabled={!hasData || !isSimulating}
            style={{ background: '#4285f4', color: '#fff', border: 'none', borderRadius: 6, padding: '8px 24px', fontSize: 18, fontWeight: 600, cursor: 'pointer', opacity: (!hasData || !isSimulating) ? 0.5 : 1 }}>
            ⏸ Pause
          </button>
          <button onClick={handleStop} disabled={!hasData || (simIndex === 0 && !isSimulating)}
            style={{ background: '#4285f4', color: '#fff', border: 'none', borderRadius: 6, padding: '8px 24px', fontSize: 18, fontWeight: 600, cursor: 'pointer', opacity: (!hasData || (simIndex === 0 && !isSimulating)) ? 0.5 : 1 }}>
            ⏹ Stop
          </button>
          <span style={{ marginLeft: 24, fontSize: 18, fontWeight: 600 }}>
            <strong>Day {hasData ? simIndex + 1 : 0}</strong> / {candles.length}
          </span>
          <label style={{ marginLeft: 16, fontSize: 16 }}>
            Speed:
            <select value={speed} onChange={e => setSpeed(Number(e.target.value))} style={{ marginLeft: 4, fontSize: 16, borderRadius: 4, padding: '2px 8px' }} disabled={!hasData}>
              <option value={1000}>1 Day = 1s</option>
              <option value={500}>1 Day = 0.5s</option>
              <option value={200}>1 Day = 0.2s</option>
            </select>
          </label>
        </div>
        <div style={{ width: '100%', maxWidth: 600, margin: '32px auto 0 auto' }}>
          <h4 style={{ textAlign: 'center', marginBottom: 8 }}>Live Cumulative PnL</h4>
          <table style={{ width: '100%', background: '#222', color: '#fff', borderRadius: 4, textAlign: 'center', fontSize: 20 }}>
            <thead>
              <tr>
                <th>Date</th>
                <th>Cumulative PnL</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>{candles[simIndex] ? new Date(candles[simIndex][0]).toLocaleDateString('en-GB') : ''}</td>
                <td style={{ color: cumPnl > 0 ? '#0f9d58' : cumPnl < 0 ? '#d93025' : '#fff', fontWeight: 700 }}>{cumPnl.toFixed(2)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

    </div>
  );
}
export default SimulateStrategy;
